import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { AnalyticsApi } from 'src/services/analyticsApi'
import { InboxPreviewSummaryResponse } from 'src/services/__generated__/api'

interface UseGetInboxPreviewSummaryParams {
  // Converted / Committed / Unconverted (pages with conversion data only).
  conversion?: 'converted' | 'committed' | 'unconverted'
  fromDate?: string
  operators?: string[]
  pageId: string
  platforms?: string[]
  // Organic / Paid (pages with ad-label attribution only).
  source?: 'organic' | 'paid'
  toDate?: string
  topics?: string[]
}

const joinCsv = (values?: string[]) =>
  values && values.length ? values.join(',') : undefined

export const useGetInboxPreviewSummaryQuery = (
  params: UseGetInboxPreviewSummaryParams
) => {
  return useQuery<InboxPreviewSummaryResponse | null>({
    enabled: !!params.pageId,
    // Keep the current summary visible while a filter change refetches, so the
    // donut/facets don't flash a full loading state on every toggle.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data } =
        await AnalyticsApi.v1Private.inboxAnalysisInboxPreviewSummaryList(
          {
            conversion: params.conversion,
            fromDate: params.fromDate,
            operators: joinCsv(params.operators),
            pageId: params.pageId,
            platforms: joinCsv(params.platforms),
            source: params.source,
            toDate: params.toDate,
            topics: joinCsv(params.topics),
          },
          { format: 'json' }
        )
      return data as InboxPreviewSummaryResponse
    },
    queryKey: [
      QueryKey.GET_INBOX_PREVIEW_SUMMARY,
      params.pageId,
      params.fromDate,
      params.toDate,
      joinCsv(params.platforms),
      joinCsv(params.topics),
      joinCsv(params.operators),
      params.source ?? 'all',
      params.conversion ?? 'all',
    ],
  })
}
